import { epochToMs, formatClockTime } from '../../../../shared/utils/date.util';
import {
  QuestionJourneyApiData,
  QuestionJourneyApiStep,
  QuestionResultApiData,
} from '../../model/test-series.model';

export type JourneyTone = 'visit' | 'correct' | 'incorrect' | 'neutral' | 'review' | 'cleared';

export interface JourneyStat {
  label: string;
  value: string;
  hint: string;
  valueTone?: JourneyTone;
  hintTone?: JourneyTone;
}

export interface JourneyEvent {
  time: string;
  title: string;
  detail: string;
  tone: JourneyTone;
}

export interface AnswerJourneyView {
  stats: JourneyStat[];
  events: JourneyEvent[];
  takeaway: string;
  takeawayTone: JourneyTone;
  isIncomplete: boolean;
}

/** Keys the backend may use for the journey object inside the question-result `data`. */
const JOURNEY_KEYS = [
  'event_summary',
  'eventSummary',
  'answer_journey',
  'answerJourney',
  'journey',
  'answer_trace',
  'answerTrace',
  'trace',
];

const SELECT_ACTIONS = new Set(['SELECT', 'SELECTED', 'ANSWER', 'ANSWERED', 'CHANGE', 'CHANGED']);
const CLEAR_ACTIONS = new Set(['CLEAR', 'CLEARED', 'DESELECT', 'UNSELECT', 'CLEAR_RESPONSE']);
const MARK_ACTIONS = new Set(['MARK', 'MARK_FOR_REVIEW', 'MARKED_FOR_REVIEW']);
const UNMARK_ACTIONS = new Set(['UNMARK', 'UNMARK_FOR_REVIEW', 'UNMARK_REVIEW']);
const CONFIRM_ACTIONS = new Set(['CONFIRM', 'CONFIRMED', 'ANSWER_CONFIRMED', 'SAVE', 'SAVED']);

type Outcome = 'right' | 'wrong' | null;

function toOutcome(part: string | undefined): Outcome {
  if (part === 'CORRECT') return 'right';
  if (part === 'INCORRECT' || part === 'WRONG') return 'wrong';
  return null;
}

/** Splits a transition such as `CORRECT_INCORRECT` into first-pick and final-answer outcomes. */
function parseTransition(transition: string | null | undefined): {
  first: Outcome;
  final: Outcome;
} {
  const parts = (transition ?? '').toUpperCase().split('_');
  return parts.length === 2
    ? { first: toOutcome(parts[0]), final: toOutcome(parts[1]) }
    : { first: null, final: null };
}

function isJourney(value: unknown): value is QuestionJourneyApiData {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return Array.isArray(v['visits']) || Array.isArray(v['steps']);
}

/** The journey may arrive as a JSON string; parse it, ignoring anything unreadable. */
function parseJourney(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** Finds the journey object in a question-result payload, by known key or by shape. */
export function findQuestionJourney(data: QuestionResultApiData): QuestionJourneyApiData | null {
  const record = data as unknown as Record<string, unknown>;
  for (const key of JOURNEY_KEYS) {
    const value = parseJourney(record[key]);
    if (isJourney(value)) return value;
  }
  const nested = Object.values(record).find(isJourney);
  if (nested) return nested;
  return isJourney(record) ? record : null;
}

/** "2m 48s", "40s", "<1s" — whole seconds, matching the admin answer journey. */
export function formatJourneyDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—';
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 1) return '<1s';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  return `${seconds}s`;
}

function humanize(value: string): string {
  const text = value.replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function stepOption(step: QuestionJourneyApiStep): string | null {
  if (step.optionIds?.length) return [...step.optionIds].sort().join(', ');
  return step.optionId || null;
}

/**
 * Builds the "Answer journey" view (stat cards, timeline and takeaway) from the
 * question-result payload. Returns null when the API sent no journey data.
 */
export function buildAnswerJourney(data: QuestionResultApiData): AnswerJourneyView | null {
  const journey = findQuestionJourney(data);
  if (!journey) return null;

  const visits = [...(journey.visits ?? [])].sort((a, b) => a.visitNo - b.visitNo);
  // Steps normally come at the top level; fall back to each visit's `selections`.
  const stepSource = journey.steps?.length
    ? journey.steps
    : visits.flatMap((visit) => visit.selections ?? []);
  const steps = [...new Map(stepSource.map((step) => [step.seq, step])).values()].sort(
    (a, b) => a.seq - b.seq,
  );
  if (!visits.length && !steps.length) return null;

  const correctKey = data.correct_key?.trim() || null;
  const correctness = (option: string | null): 'correct' | 'incorrect' | null =>
    !option || !correctKey ? null : option === correctKey ? 'correct' : 'incorrect';
  const finalAnswer = journey.final_answer_set?.length
    ? [...journey.final_answer_set].sort().join(', ')
    : journey.final_answer || null;

  // ── Timeline: visits and steps merged in time order ──
  type Timed = JourneyEvent & { at: number; order: number };
  const timed: Timed[] = [];
  let order = 0;
  const push = (atSeconds: number | null | undefined, event: JourneyEvent) => {
    const at = epochToMs(atSeconds);
    timed.push({
      ...event,
      time: at ? formatClockTime(at) : '',
      at: at ?? Number.MAX_SAFE_INTEGER,
      order: order++,
    });
  };
  const join = (...parts: string[]) => parts.filter(Boolean).join(' · ');
  const correctnessLabel = (result: 'correct' | 'incorrect' | null) =>
    result === 'correct' ? 'Correct' : result === 'incorrect' ? 'Incorrect' : '';

  const stepsByVisit = new Map<number, number>();
  for (const step of steps) {
    if (step.visitNo != null)
      stepsByVisit.set(step.visitNo, (stepsByVisit.get(step.visitNo) ?? 0) + 1);
  }

  for (const visit of visits) {
    const total = visit.totalMs ?? null;
    const active =
      visit.activeMs != null && total != null && visit.activeMs < total
        ? `${formatJourneyDuration(visit.activeMs)} active`
        : '';
    const closedBy = visit.closedBy?.toUpperCase();
    const noChange = !stepsByVisit.get(visit.visitNo) && !visit.selections?.length;
    push(visit.enteredAt, {
      time: '',
      title: visit.visitNo === 1 ? 'Visit 1 — first look' : `Visit ${visit.visitNo} — came back`,
      detail: join(
        `Stayed ${formatJourneyDuration(total ?? visit.activeMs)}${noChange ? ', no change' : ''}`,
        active,
        closedBy && closedBy !== 'EXITED' ? humanize(closedBy) : '',
      ),
      tone: 'visit',
    });
  }

  let current: string | null = null;
  let firstPick: string | null = null;
  let firstAnswerVisit: number | null = null;

  for (const step of steps) {
    const action = (step.action || '').toUpperCase();
    const option = stepOption(step);
    const gap = step.msSincePrevStep
      ? `${formatJourneyDuration(step.msSincePrevStep)} after previous step`
      : '';
    const intoVisit =
      step.timeToSelectMs != null
        ? `${formatJourneyDuration(step.timeToSelectMs)} into visit ${step.visitNo ?? ''}`.trim()
        : gap;
    const review = step.markedForReview && !MARK_ACTIONS.has(action) ? 'Marked for review' : '';
    let title: string;
    let detail: string;
    let tone: JourneyTone = 'neutral';

    if (SELECT_ACTIONS.has(action) && option) {
      const result = correctness(option);
      tone = result ?? 'neutral';
      if (current && current !== option) {
        title = `Changed ${current} → ${option}`;
        detail = join(correctnessLabel(result), intoVisit, review);
      } else {
        const isFirst = firstPick === null;
        title = `Selected ${option}`;
        detail = join(
          isFirst && result
            ? `First pick — ${result}`
            : isFirst
              ? 'First pick'
              : correctnessLabel(result),
          intoVisit,
          review,
        );
      }
      if (firstPick === null) {
        firstPick = option;
        firstAnswerVisit = step.visitNo ?? null;
      }
      current = option;
    } else if (CONFIRM_ACTIONS.has(action)) {
      // ANSWER_CONFIRMED: Save & Next / Mark for Review & Next (optionId null = saved blank).
      const marked = !!step.markedForReview;
      title = option
        ? `Confirmed ${option}${marked ? ' · marked for review' : ''}`
        : `Saved with no answer${marked ? ' · marked for review' : ''}`;
      detail = join(
        marked ? 'Moved on with Mark for Review & Next' : 'Moved on with Save & Next',
        gap,
      );
      tone = 'review';
      current = option;
    } else if (CLEAR_ACTIONS.has(action)) {
      title = current ? `Cleared ${current}` : 'Cleared response';
      detail = join('Answer removed', gap);
      tone = 'cleared';
      current = null;
    } else if (MARK_ACTIONS.has(action)) {
      title = 'Marked for review';
      detail = gap;
      tone = 'review';
    } else if (UNMARK_ACTIONS.has(action)) {
      title = 'Unmarked for review';
      detail = gap;
      tone = 'review';
    } else {
      title = option ? `${humanize(action || 'Event')} ${option}` : humanize(action || 'Event');
      detail = gap;
    }

    push(step.at, { time: '', title, detail, tone });
  }

  // Closing row: the answer that was submitted for this question.
  const lastExit = Math.max(
    0,
    ...visits.map((visit) => visit.exitedAt ?? visit.enteredAt ?? 0),
    ...steps.map((step) => step.at ?? 0),
  );
  const finalLabel = correctnessLabel(correctness(finalAnswer));
  push(lastExit || null, {
    time: '',
    title: finalAnswer ? `Final answer recorded: ${finalAnswer}` : 'No answer recorded',
    detail: finalAnswer
      ? join(finalLabel, journey.was_guessing ? 'Marked as a guess' : '')
      : 'Left unanswered',
    tone: finalAnswer ? (correctness(finalAnswer) ?? 'neutral') : 'cleared',
  });
  // Keep it last even when it shares a timestamp with the final step.
  timed[timed.length - 1].at += 0.5;

  timed.sort((a, b) => a.at - b.at || a.order - b.order);
  const events: JourneyEvent[] = timed.map(({ time, title, detail, tone }) => ({
    time,
    title,
    detail,
    tone,
  }));

  // ── Stat cards ──
  // Picks in order, consecutive repeats collapsed: A, B, A → "A → B → A".
  const optionPath: string[] = [];
  for (const step of steps) {
    const action = (step.action || '').toUpperCase();
    const option = stepOption(step);
    if (!option || !(SELECT_ACTIONS.has(action) || CONFIRM_ACTIONS.has(action))) continue;
    if (optionPath[optionPath.length - 1] !== option) optionPath.push(option);
  }
  const finalResult = correctness(finalAnswer);
  const changeCount = journey.change_count ?? 0;
  const visitCount = journey.visit_count ?? visits.length;
  const totalMs = journey.total_time_ms ?? null;
  const activeMs = journey.active_time_ms ?? null;
  const { first: firstOutcome, final: finalOutcome } = parseTransition(journey.transition);
  const transitionTone: JourneyTone | undefined =
    firstOutcome === 'right' && finalOutcome === 'wrong'
      ? 'incorrect'
      : firstOutcome === 'wrong' && finalOutcome === 'right'
        ? 'correct'
        : undefined;

  const stats: JourneyStat[] = [
    {
      label: 'Time on question',
      value: formatJourneyDuration(totalMs ?? activeMs),
      hint:
        activeMs != null && totalMs != null && activeMs < totalMs
          ? `${formatJourneyDuration(activeMs)} active`
          : 'All time active',
    },
    {
      label: 'Visits',
      value: String(visitCount),
      hint: firstAnswerVisit ? `Answered on visit ${firstAnswerVisit}` : 'Never answered',
    },
    {
      label: 'Option changes',
      value: optionPath.length > 1 ? optionPath.join(' → ') : (finalAnswer ?? '—'),
      hint: join(
        `${changeCount} ${changeCount === 1 ? 'change' : 'changes'}`,
        firstOutcome && finalOutcome ? `${firstOutcome} → ${finalOutcome}` : '',
      ),
      hintTone: transitionTone,
    },
    {
      label: 'Time to first answer',
      value: formatJourneyDuration(journey.time_to_first_answer_ms),
      hint:
        journey.time_after_final_answer_ms != null
          ? `${formatJourneyDuration(journey.time_after_final_answer_ms)} spent after final answer`
          : '',
    },
  ];
  firstPick ??= finalAnswer;

  // ── Takeaway ──
  let takeaway: string;
  let takeawayTone: JourneyTone = 'neutral';
  if (!finalAnswer && !steps.length) {
    takeaway = `You opened this question ${visitCount} ${visitCount === 1 ? 'time' : 'times'} but never picked an option.`;
  } else if (!finalAnswer) {
    takeaway =
      'You picked an option at some point but cleared it, so the question was left unanswered.';
  } else if (changeCount === 0) {
    const when = !firstAnswerVisit
      ? ''
      : firstAnswerVisit > 1
        ? ` on visit ${firstAnswerVisit}`
        : ' on your first visit';
    takeaway = `You picked ${finalAnswer}${when} and never changed it`;
    takeaway += finalResult ? ` — it was ${finalResult}.` : '.';
    takeawayTone = finalResult ?? 'neutral';
  } else {
    switch (firstOutcome && finalOutcome ? `${firstOutcome}_${finalOutcome}` : '') {
      case 'right_wrong':
        takeaway =
          firstPick === correctKey
            ? `Your first pick (${firstPick}) was correct, but you changed it to ${finalAnswer}. Trust your first instinct unless you find a clear reason to switch.`
            : `You had the correct answer (${correctKey}) at one point but moved away from it and submitted ${finalAnswer}.`;
        takeawayTone = 'incorrect';
        break;
      case 'wrong_right':
        takeaway = `You started with ${firstPick} and corrected it to ${finalAnswer} — the change paid off.`;
        takeawayTone = 'correct';
        break;
      case 'right_right':
        takeaway = `You changed your answer ${changeCount} ${changeCount === 1 ? 'time' : 'times'} but ended on the correct option (${finalAnswer}).`;
        takeawayTone = 'correct';
        break;
      default:
        takeaway = `You changed from ${firstPick} to ${finalAnswer}, but neither was correct${correctKey ? ` (correct answer: ${correctKey})` : ''}.`;
        takeawayTone = 'incorrect';
    }
  }
  if (journey.was_guessing) takeaway += ' You marked this answer as a guess.';

  return {
    stats,
    events,
    takeaway,
    takeawayTone,
    isIncomplete: !!(journey.incomplete || journey.trace_incomplete || journey.timing_incomplete),
  };
}
